import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import Panel from '../components/Panel';

const SECTIONS = ['Overview', 'Drivers', 'Satisfaction', 'Tenure', 'Manager'];

const COLORS = ['#1f5eff', '#1f7a3f', '#c98b12', '#c2410c', '#7c3aed', '#0f766e', '#1a4fd6', '#e67e22'];

// Plotly background — matches every FieldIntel chart
const BG = {
  plot_bgcolor: 'var(--bg-dark)',
  paper_bgcolor: 'var(--card-bg)',
  font: { family: 'inherit', size: 12 },
};

const plotCfg = {
  responsive: true,
  displaylogo: false,
  modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'],
};

// Standard 2-col auto-fit grid
const GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
  gap: '24px',
  marginBottom: '32px',
};

// ---------------------------------------------------------------------------
// Micro-components
// ---------------------------------------------------------------------------

function KpiCard({ value, label, note, accent = 'blue' }) {
  const accentColors = {
    blue: '#1f5eff', green: '#1f7a3f', amber: '#c98b12',
    teal: '#0f766e', red: '#c2410c', purple: '#7c3aed',
  };
  return (
    <div className="metric-card">
      <span className="metric-label">{label}</span>
      <div className="metric-value" style={{ color: accentColors[accent] || accentColors.blue }}>{value}</div>
      {note && <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>{note}</div>}
    </div>
  );
}

function DataSourceTag({ source, n }) {
  const live = source === 'live';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      fontFamily: 'Arial, Helvetica, sans-serif',
      background: live ? 'rgba(31,122,63,0.10)' : 'rgba(201,139,18,0.12)',
      color: live ? '#1f7a3f' : '#c98b12',
      marginLeft: 8,
    }}>
      <span style={{ fontSize: 7 }}>{live ? '●' : '◯'}</span>
      {live ? `Live · n=${n || '—'}` : 'Survey data'}
    </span>
  );
}

function Callout({ text }) {
  return (
    <div style={{
      borderLeft: '4px solid #c2410c',
      background: 'rgba(194,65,12,0.06)',
      padding: '10px 14px',
      borderRadius: '0 8px 8px 0',
      fontSize: 13, fontWeight: 600,
      color: 'var(--text-dark)',
      margin: '12px 0 8px',
      lineHeight: 1.5,
    }}>
      {text}
    </div>
  );
}

function SectionTabs({ active, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap',
      paddingBottom: 12, borderBottom: '1px solid var(--card-border)',
    }}>
      {SECTIONS.map(s => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          style={{
            padding: '7px 18px',
            border: '1px solid var(--card-border)',
            background: active === s ? 'var(--text-dark)' : 'var(--card-bg)',
            cursor: 'pointer',
            fontWeight: active === s ? 700 : 500,
            color: active === s ? '#fff' : 'var(--text-light)',
            fontSize: 13,
            borderRadius: 8,
            fontFamily: 'Arial, Helvetica, sans-serif',
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function NoData({ text = 'No data' }) {
  return (
    <div style={{ color: 'var(--text-lighter)', padding: '40px 0', textAlign: 'center', fontSize: 13 }}>
      {text}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function HrRetention({ filters, loading, setLoading }) {
  const [section, setSection] = useState('Overview');

  // Overview
  const [kpis, setKpis] = useState(null);
  const [byInterviewer, setByInterviewer] = useState({ labels: [], data: [] });
  const [attritionType, setAttritionType] = useState({ labels: [], data: [] });
  const [byState, setByState] = useState({ labels: [], data: [] });
  const [topicDist, setTopicDist] = useState({ labels: [], data: [] });

  // Drivers
  const [reasons, setReasons] = useState({ labels: [], data: [], confidence: [] });
  const [reasonsByState, setReasonsByState] = useState({ states: [], datasets: [] });
  const [reasonsByRo, setReasonsByRo] = useState({ ros: [], reasons: [], values: [] });
  const [nonCoreBurden, setNonCoreBurden] = useState({ labels: [], data: [] });
  const [employmentType, setEmploymentType] = useState({ labels: [], data: [] });

  // Satisfaction
  const [managerAttr, setManagerAttr] = useState({ labels: [], data: [], n: [], question_ids: [], source: 'live' });
  const [sdtScores, setSdtScores] = useState({ needs: [], frustration: [], satisfaction: [] });
  const [careerPipeline, setCareerPipeline] = useState({ stages: [], values: [], totals: [], source: 'live' });
  const [payHike, setPayHike] = useState({ zones: [], bands: [], values: [] });

  // Tenure
  const [tenureDist, setTenureDist] = useState({ labels: [], data: [] });
  const [tenureReason, setTenureReason] = useState({ tenures: [], reasons: [], values: [] });
  const [demographics, setDemographics] = useState({
    education: { labels: [], data: [] },
    marital:   { labels: [], data: [] },
    age:       { labels: [], data: [] },
  });
  const [payDist, setPayDist] = useState([]);

  // Manager
  const [zoneReasons, setZoneReasons] = useState({ zones: [], reasons: [], values: [] });
  const [quarterlyTrend, setQuarterlyTrend] = useState({ quarters: [], zones: [], values: [] });
  const [byRo, setByRo] = useState({ labels: [], state: [], data: [] });
  const [entityBreakdown, setEntityBreakdown] = useState({ labels: [], data: [] });

  useEffect(() => {
    setLoading(true);
    const base = '/api/hr/retention';

    const safe = (promise) => promise.catch(() => ({ data: null }));

    Promise.all([
      safe(axios.get(`${base}/kpis`)),
      safe(axios.get(`${base}/interviews-by-interviewer`)),
      safe(axios.get(`${base}/attrition-type`)),
      safe(axios.get(`${base}/interviews-by-state`)),
      safe(axios.get(`${base}/topic-distribution`)),
      safe(axios.get(`${base}/attrition-reasons`)),
      safe(axios.get(`${base}/attrition-reasons-by-state`)),
      safe(axios.get(`${base}/attrition-reasons-by-ro`)),
      safe(axios.get(`${base}/non-core-burden`)),
      safe(axios.get(`${base}/employment-type`)),
      safe(axios.get(`${base}/manager-attributes`)),
      safe(axios.get(`${base}/sdt-scores`)),
      safe(axios.get(`${base}/career-pipeline`)),
      safe(axios.get(`${base}/pay-hike-by-zone`)),
      safe(axios.get(`${base}/tenure-distribution`)),
      safe(axios.get(`${base}/tenure-reason-heatmap`)),
      safe(axios.get(`${base}/demographics`)),
      safe(axios.get(`${base}/pay-distribution`)),
      safe(axios.get(`${base}/zone-reasons`)),
      safe(axios.get(`${base}/quarterly-trend`)),
      safe(axios.get(`${base}/interviews-by-ro`)),
      safe(axios.get(`${base}/entity-type-breakdown`)),
    ])
      .then(([
        kpiR, intR, attrR, stateR, topicR,
        reasonR, rsStateR, rsRoR, ncR, etR,
        mgrR, sdtR, careerR, payHikeR,
        tenureR, trHeatR, demoR, payDistR,
        zrR, qtR, roR, ebR,
      ]) => {
        if (kpiR.data)      setKpis(kpiR.data);
        if (intR.data)      setByInterviewer(intR.data);
        if (attrR.data)     setAttritionType(attrR.data);
        if (stateR.data)    setByState(stateR.data);
        if (topicR.data)    setTopicDist(topicR.data);
        if (reasonR.data)   setReasons(reasonR.data);
        if (rsStateR.data)  setReasonsByState(rsStateR.data);
        if (rsRoR.data)     setReasonsByRo(rsRoR.data);
        if (ncR.data)       setNonCoreBurden(ncR.data);
        if (etR.data)       setEmploymentType(etR.data);
        if (mgrR.data)      setManagerAttr(mgrR.data);
        if (sdtR.data)      setSdtScores(sdtR.data);
        if (careerR.data)   setCareerPipeline(careerR.data);
        if (payHikeR.data)  setPayHike(payHikeR.data);
        if (tenureR.data)   setTenureDist(tenureR.data);
        if (trHeatR.data)   setTenureReason(trHeatR.data);
        if (demoR.data)     setDemographics(demoR.data);
        if (payDistR.data)  setPayDist(payDistR.data);
        if (zrR.data)       setZoneReasons(zrR.data);
        if (qtR.data)       setQuarterlyTrend(qtR.data);
        if (roR.data)       setByRo(roR.data);
        if (ebR.data)       setEntityBreakdown(ebR.data);
      })
      .finally(() => setLoading(false));
  }, [setLoading]);

  // ---------------------------------------------------------------------------
  // Section renders
  // ---------------------------------------------------------------------------

  const renderOverview = () => (
    <div>
      {kpis && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <KpiCard value={kpis.total_interviews} label="Exit Interviews"   accent="blue"   />
          <KpiCard value={kpis.interviewers}     label="Interviewers"      accent="green"  />
          <KpiCard value={kpis.regions_covered}  label="Regional Offices"  accent="amber"  />
          <KpiCard value={kpis.entity_mentions}  label="Entity Mentions"   accent="purple" />
        </div>
      )}

      <div style={GRID}>
        <Panel loading={loading} title="Interviews by Interviewer">
          {byInterviewer.labels.length > 0 ? (
            <Plot
              data={[{ x: byInterviewer.data, y: byInterviewer.labels, type: 'bar', orientation: 'h', marker: { color: '#1f5eff' } }]}
              layout={{ ...BG, margin: { t: 20, r: 40, b: 40, l: 200 }, height: 260 }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Attrition Type">
          {attritionType.labels.length > 0 ? (
            <Plot
              data={[{ labels: attritionType.labels, values: attritionType.data, type: 'pie', hole: 0.45, marker: { colors: ['#1f7a3f', '#c2410c', '#c98b12'] } }]}
              layout={{ ...BG, showlegend: true, height: 300, margin: { t: 20, b: 60, l: 20, r: 20 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Interviews by State">
          {byState.labels.length > 0 ? (
            <Plot
              data={[{ x: byState.data, y: byState.labels, type: 'bar', orientation: 'h', marker: { color: '#1f7a3f' } }]}
              layout={{ ...BG, margin: { t: 20, r: 40, b: 40, l: 140 }, height: 320 }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Topic Distribution">
          {topicDist.labels.length > 0 ? (
            <Plot
              data={[{
                labels: topicDist.labels, values: topicDist.data, type: 'pie', hole: 0.4,
                marker: { colors: COLORS }, textinfo: 'percent', textposition: 'inside', insidetextorientation: 'radial',
              }]}
              layout={{ ...BG, height: 380, showlegend: true, legend: { orientation: 'h', x: 0, y: -0.2, xanchor: 'left' }, margin: { t: 20, b: 120, l: 20, r: 20 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>
    </div>
  );

  const renderDrivers = () => (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Attrition Reasons — Unique Interviews" subtitle="Each bar = distinct interviews mentioning this reason (not mention count)">
          {reasons.labels.length > 0 ? (
            <Plot
              data={[{
                x: reasons.data, y: reasons.labels, type: 'bar', orientation: 'h',
                marker: { color: reasons.confidence.map(c => c >= 0.85 ? '#1f7a3f' : c >= 0.75 ? '#1f5eff' : '#c2410c') },
                hovertemplate: '%{y}<br>Interviews: %{x}<extra></extra>',
              }]}
              layout={{ ...BG, height: Math.max(360, reasons.labels.length * 32), margin: { t: 20, r: 40, b: 50, l: 200 }, xaxis: { title: 'Mentions' } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={GRID}>
        <Panel loading={loading} title="Attrition Reasons by State">
          {reasonsByState.states && reasonsByState.states.length > 0 ? (
            <Plot
              data={(reasonsByState.datasets || []).map((ds, i) => ({
                x: reasonsByState.states, y: ds.data, name: ds.label, type: 'bar',
                marker: { color: COLORS[i % COLORS.length] },
              }))}
              layout={{
                ...BG, barmode: 'group', height: 360,
                xaxis: { automargin: true, tickangle: -30 }, yaxis: { title: 'Mentions' },
                legend: { orientation: 'h', y: -0.35, x: 0 }, margin: { t: 20, r: 20, b: 120, l: 60 },
              }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Employment Type" subtitle="Extracted from interviews">
          {employmentType.labels.length > 0 ? (
            <Plot
              data={[{ x: employmentType.labels, y: employmentType.data, type: 'bar', marker: { color: '#1f5eff' }, hovertemplate: '%{x}<br>Mentions: %{y}<extra></extra>' }]}
              layout={{ ...BG, xaxis: { automargin: true, tickangle: -20 }, yaxis: { title: 'Mentions' }, margin: { t: 20, r: 20, b: 60, l: 60 }, height: 300 }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Attrition Reasons by Regional Office">
          {reasonsByRo.ros && reasonsByRo.ros.length > 0 ? (
            <Plot
              data={[{ z: reasonsByRo.values, x: reasonsByRo.reasons, y: reasonsByRo.ros, type: 'heatmap', colorscale: 'YlOrRd', hovertemplate: 'RO: %{y}<br>Reason: %{x}<br>Mentions: %{z}<extra></extra>' }]}
              layout={{ ...BG, height: Math.max(400, reasonsByRo.ros.length * 34), margin: { t: 20, r: 20, b: 120, l: 160 }, xaxis: { automargin: true, tickangle: -35, tickfont: { size: 11 } } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Non-Core Activity Burden" subtitle="% of MGOs performing each activity — survey data">
          {nonCoreBurden.labels.length > 0 ? (
            <Plot
              data={[{ x: nonCoreBurden.data, y: nonCoreBurden.labels, type: 'bar', orientation: 'h', marker: { color: '#c2410c' }, hovertemplate: '%{y}: %{x}%<extra></extra>' }]}
              layout={{ ...BG, height: 280, margin: { t: 20, r: 40, b: 50, l: 200 }, xaxis: { title: '% of MGOs', range: [0, 105] } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>
    </div>
  );

  const renderSatisfaction = () => (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Panel
          loading={loading}
          title="Manager Interaction Quality — Ranked Low to High"
          subtitle={<>1 = Strongly Disagree · 5 = Strongly Agree · dotted line = neutral (3.0) <DataSourceTag source={managerAttr.source} n={managerAttr.n && managerAttr.n[0]} /></>}
        >
          {managerAttr.labels.length > 0 ? (
            <Plot
              data={[{
                x: managerAttr.data, y: managerAttr.labels, type: 'bar', orientation: 'h',
                marker: { color: managerAttr.data.map(v => v < 3.0 ? '#c2410c' : v < 3.5 ? '#c98b12' : '#1f7a3f') },
                hovertemplate: '%{y}: %{x:.2f} / 5<extra></extra>',
              }]}
              layout={{
                ...BG, height: 420, margin: { t: 20, r: 40, b: 50, l: 220 },
                xaxis: { title: 'Score (1–5)', range: [0, 5], tickvals: [0,1,2,3,4,5] },
                shapes: [{ type: 'line', x0: 3, x1: 3, y0: -0.5, y1: managerAttr.labels.length - 0.5, line: { color: '#ded8c8', dash: 'dot', width: 1.5 } }],
              }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={GRID}>
        <Panel loading={loading} title="Psychological Needs — Satisfaction vs Frustration" subtitle="Self-Determination Theory · survey data (n=39)">
          {sdtScores.needs && sdtScores.needs.length > 0 ? (
            <Plot
              data={[
                { x: sdtScores.frustration, y: sdtScores.needs, name: 'Frustration', type: 'scatter', mode: 'markers', marker: { color: '#c2410c', size: 14 } },
                { x: sdtScores.satisfaction, y: sdtScores.needs, name: 'Satisfaction', type: 'scatter', mode: 'markers', marker: { color: '#1f7a3f', size: 14 } },
                ...sdtScores.needs.map((need, i) => ({
                  x: [sdtScores.frustration[i], sdtScores.satisfaction[i]], y: [need, need],
                  type: 'scatter', mode: 'lines', line: { color: '#ded8c8', width: 2 }, showlegend: false,
                })),
              ]}
              layout={{
                ...BG, height: 320, margin: { t: 20, b: 120, l: 120, r: 24 },
                xaxis: { title: 'Score (1–5)', range: [1, 5] },
                showlegend: true, legend: { orientation: 'h', y: -0.35, x: 0 },
              }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel
          loading={loading}
          title="Absorption into Direct Payroll"
          subtitle={<>% of MGOs who answered Yes to each question <DataSourceTag source={careerPipeline.source} n={careerPipeline.totals && careerPipeline.totals[0]} /></>}
        >
          {careerPipeline.values.length > 5 && (
            <Callout text={`${careerPipeline.values[0]}% aspired to direct payroll — only ${careerPipeline.values[5]}% received assessment feedback`} />
          )}
          {careerPipeline.stages && careerPipeline.stages.length > 0 ? (
            <Plot
              data={[{
                x: careerPipeline.values,
                y: careerPipeline.stages,
                type: 'bar',
                orientation: 'h',
                marker: {
                  color: careerPipeline.values.map(v =>
                    v >= 60 ? '#1f7a3f' : v >= 30 ? '#c98b12' : '#c2410c'
                  ),
                },
                text: careerPipeline.values.map(v => `${v}%`),
                textposition: 'outside',
                hovertemplate: '%{y}<br>%{x}% said Yes<extra></extra>',
              }]}
              layout={{
                ...BG,
                height: 320,
                margin: { t: 20, r: 60, b: 50, l: 280 },
                xaxis: { title: '% who said Yes', range: [0, 110] },
              }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Post-Exit Pay Hike Distribution by Zone" subtitle="% of employees in each pay-hike band · survey data">
          {payHike.zones && payHike.zones.length > 0 ? (
            <Plot
              data={(payHike.bands || []).map((band, i) => ({
                name: band, x: payHike.zones, y: payHike.values.map(zv => zv[i]),
                type: 'bar', marker: { color: COLORS[i % COLORS.length] },
              }))}
              layout={{
                ...BG, barmode: 'stack', yaxis: { title: '% of MGOs', range: [0, 105] },
                legend: { orientation: 'h', y: -0.25, x: 0 }, margin: { t: 20, r: 20, b: 100, l: 60 }, height: 360,
              }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>
    </div>
  );

  const renderTenure = () => (
    <div>
      <div style={GRID}>
        <Panel loading={loading} title="Tenure Distribution at Exit">
          {tenureDist.labels.length > 0 ? (
            <Plot
              data={[{ x: tenureDist.labels, y: tenureDist.data, type: 'bar', marker: { color: '#1f5eff' } }]}
              layout={{ ...BG, xaxis: { automargin: true, tickangle: -25 }, yaxis: { title: 'Employees' }, margin: { t: 20, r: 20, b: 80, l: 60 }, height: 300 }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Education Level">
          {demographics.education.labels.length > 0 ? (
            <Plot
              data={[{ labels: demographics.education.labels, values: demographics.education.data, type: 'pie', hole: 0.4, marker: { colors: COLORS } }]}
              layout={{ ...BG, showlegend: true, height: 300, margin: { t: 20, b: 40, l: 20, r: 20 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Marital Status">
          {demographics.marital.labels.length > 0 ? (
            <Plot
              data={[{ x: demographics.marital.labels, y: demographics.marital.data, type: 'bar', marker: { color: ['#1f5eff', '#1f7a3f', '#c98b12'] } }]}
              layout={{ ...BG, height: 280, yaxis: { title: 'Employees' }, xaxis: { automargin: true }, margin: { t: 20, r: 20, b: 50, l: 60 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Age Group Distribution">
          {demographics.age.labels.length > 0 ? (
            <Plot
              data={[{ x: demographics.age.labels, y: demographics.age.data, type: 'bar', marker: { color: '#7c3aed' } }]}
              layout={{ ...BG, height: 280, yaxis: { title: 'Employees' }, xaxis: { automargin: true }, margin: { t: 20, r: 20, b: 50, l: 60 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Attrition Reason by Tenure Band">
          {tenureReason.tenures && tenureReason.tenures.length > 0 ? (
            <Plot
              data={[{ z: tenureReason.values, x: tenureReason.reasons, y: tenureReason.tenures, type: 'heatmap', colorscale: 'Blues', hovertemplate: 'Tenure: %{y}<br>Reason: %{x}<br>Mentions: %{z}<extra></extra>' }]}
              layout={{ ...BG, height: Math.max(320, tenureReason.tenures.length * 40), margin: { t: 20, b: 140, l: 160, r: 24 }, xaxis: { automargin: true, tickangle: -40, tickfont: { size: 11 } } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Net Pay Distribution by State" subtitle="Monthly net salary (₹) · box shows Q1–Q3, whiskers show min/max">
          {payDist.length > 0 ? (
            <Plot
              data={payDist.map((s, i) => ({
                type: 'box', name: `${s.state} (n=${s.n})`,
                q1: [s.q1], median: [s.median], q3: [s.q3],
                lowerfence: [s.min], upperfence: [s.max], mean: [s.mean],
                marker: { color: COLORS[i % COLORS.length] }, boxmean: true,
              }))}
              layout={{ ...BG, height: 380, yaxis: { title: 'Net Pay (₹)' }, boxmode: 'group', margin: { t: 20, r: 20, b: 60, l: 80 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>
    </div>
  );

  const renderManager = () => (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Attrition Reasons by Zone" subtitle="% citing as main reason for leaving · survey data">
          {zoneReasons.zones && zoneReasons.zones.length > 0 ? (
            <Plot
              data={(zoneReasons.reasons || []).map((reason, i) => ({
                name: reason, x: zoneReasons.zones, y: zoneReasons.values[i], type: 'bar',
                marker: { color: COLORS[i % COLORS.length] },
              }))}
              layout={{
                ...BG, barmode: 'group', yaxis: { title: '% citing reason' },
                legend: { orientation: 'h', y: -0.25, x: 0 }, margin: { t: 20, r: 20, b: 100, l: 60 }, height: 380,
              }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Panel loading={loading} title="Quarterly Attrition Trend by Zone" subtitle="% of annual exits per quarter · survey data">
          {quarterlyTrend.quarters && quarterlyTrend.quarters.length > 0 ? (
            <Plot
              data={(quarterlyTrend.zones || []).map((zone, i) => ({
                name: zone, x: quarterlyTrend.quarters, y: quarterlyTrend.values[i],
                type: 'scatter', mode: 'lines+markers',
                line: { color: COLORS[i % COLORS.length] }, marker: { color: COLORS[i % COLORS.length], size: 8 },
              }))}
              layout={{
                ...BG, yaxis: { title: '% of exits' },
                legend: { orientation: 'h', y: -0.25, x: 0 }, margin: { t: 20, r: 20, b: 100, l: 60 }, height: 360,
              }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>

      <div style={GRID}>
        <Panel loading={loading} title="Interviews by Regional Office">
          {byRo.labels && byRo.labels.length > 0 ? (
            <Plot
              data={[{
                x: byRo.data, y: byRo.labels, type: 'bar', orientation: 'h',
                marker: { color: '#1f5eff' },
                hovertemplate: '%{y} (%{customdata})<br>Interviews: %{x}<extra></extra>',
                customdata: byRo.state || [],
              }]}
              layout={{ ...BG, height: Math.max(400, (byRo.labels || []).length * 30), margin: { t: 20, r: 40, b: 50, l: 160 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>

        <Panel loading={loading} title="Entity Type Breakdown">
          {entityBreakdown.labels.length > 0 ? (
            <Plot
              data={[{ labels: entityBreakdown.labels, values: entityBreakdown.data, type: 'pie', hole: 0.35, marker: { colors: COLORS }, textinfo: 'percent', textposition: 'inside' }]}
              layout={{ ...BG, showlegend: true, height: 420, margin: { t: 20, b: 20, l: 20, r: 20 } }}
              config={plotCfg}
              style={{ width: '100%' }}
            />
          ) : <NoData />}
        </Panel>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (section) {
      case 'Overview':     return renderOverview();
      case 'Drivers':      return renderDrivers();
      case 'Satisfaction': return renderSatisfaction();
      case 'Tenure':       return renderTenure();
      case 'Manager':      return renderManager();
      default:             return renderOverview();
    }
  };

  return (
    <div>
      <div className="module-header">
        <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
          Exit Interview Analytics
        </div>
        <h2>Retention Intelligence</h2>
        <p>{kpis ? kpis.total_interviews : '—'} exit interviews · {kpis ? kpis.entity_mentions : '—'} entity mentions</p>
      </div>

      <SectionTabs active={section} onChange={setSection} />
      {renderSection()}
    </div>
  );
}

export default HrRetention;
